import { DocumentQuery, IDocumentService, SortOrder } from '@price-for-profit/micro-services';
import { CLIENT_ID, DRAFT_CONTRACT_DOCUMENT_TYPE } from 'shared/constants/app';
import { ContractDocument, SortableDraftContractFields } from 'shared/types';
import { isAxiosError } from 'shared/utility';

export interface IDraftContractService {
  createNewDraftContract: (contractTerms: ContractDocument) => Promise<ContractDocument>;
  deleteDraftContract(id: string): Promise<void>;
  updateDraftContract: (contractTerms: ContractDocument) => Promise<{ operationId: string }>;
  getDraftContract: (id: string) => Promise<ContractDocument | undefined>;
  getDraftContracts: (
    showTermOrTierChanges: boolean,
    showDeletionsOrRestorations: boolean,
    baseTableProps: BaseTableProps
  ) => Promise<GetDraftContractsResponse>;
}

export interface GetDraftContractsResponse {
  contracts: ContractDocument[];
  totalCount: number;
  totalPages: number | undefined;
  currentPage: number;
}

export interface BaseTableProps {
  page?: number;
  pageSize?: number;
  sortOrder?: SortOrder;
  sortBy?: SortableDraftContractFields;
}

export class DraftContractService implements IDraftContractService {
  private shared: { type: string; clientId: string } = { type: DRAFT_CONTRACT_DOCUMENT_TYPE, clientId: CLIENT_ID };
  constructor(private documentService: IDocumentService) {}
  async deleteDraftContract(id: string): Promise<void> {
    await this.documentService.delete({
      ...this.shared,
      id,
    });
  }

  async createNewDraftContract(contract: ContractDocument): Promise<ContractDocument> {
    const { data } = await this.documentService.create<ContractDocument, ContractDocument>({
      ...this.shared,
      payload: contract,
    });

    return data;
  }

  async updateDraftContract(contract: ContractDocument): Promise<{ operationId: string }> {
    const { metadata } = await this.documentService.update<ContractDocument>({
      ...this.shared,
      id: contract.id,
      payload: contract,
    });

    return metadata;
  }

  async getDraftContract(id: string): Promise<ContractDocument | undefined> {
    try {
      const { data } = await this.documentService.getById<ContractDocument>({
        clientId: CLIENT_ID,
        type: DRAFT_CONTRACT_DOCUMENT_TYPE,
        id,
      });
      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          return undefined;
        }
        throw error;
      }
      throw error;
    }
  }

  async getDraftContracts(
    showTermOrTierChanges: boolean,
    showDeletionsOrRestorations: boolean,
    baseTableProps: BaseTableProps
  ): Promise<GetDraftContractsResponse> {
    const query: DocumentQuery<ContractDocument> = {
      sortBy: baseTableProps.sortBy ?? undefined,
      sortOrder: baseTableProps.sortOrder,
      page: baseTableProps.page === undefined ? undefined : baseTableProps.page, // page in the DocService is zero-based.
      pageSize: baseTableProps.pageSize === undefined ? undefined : baseTableProps.pageSize,
      document:
        showTermOrTierChanges && showDeletionsOrRestorations
          ? {}
          : showDeletionsOrRestorations
          ? { markedForDeletionOrRestorationOn: [{ operator: 'ne', value: '' }] }
          : showTermOrTierChanges
          ? { markedForDeletionOrRestorationOn: [{ operator: 'eq', value: '' }] }
          : { markedForDeletionOrRestorationOn: [{ operator: 'eq', value: 'invalid filter' }] },
    };

    const { data, metadata } = await this.documentService.query<ContractDocument>({
      clientId: CLIENT_ID,
      type: DRAFT_CONTRACT_DOCUMENT_TYPE,
      payload: query,
    });
    const { totalCount, totalPages, currentPage } = metadata;

    return { contracts: data, totalCount, totalPages, currentPage };
  }
}
